import * as React from 'react';

import type { ColumnFiltersState } from '@tanstack/react-table';
import type { ColumnFilterShowDropdownState } from 'src/registry/components/table/types';
import { useSearchParams } from 'react-router-dom';

type FilterObject = {
    value: {
        beforeDate: Date;
        afterDate: Date;
        beforeTime: string;
        afterTime: string;
    };
    id: string;
};

interface useFiltersProps {
    initialQueryFilters?: Record<string, string>;
}

function useFilters({ initialQueryFilters = {} }: useFiltersProps = {}) {
    const [searchParams] = useSearchParams();

    // This is in order to enable the custom logic to have dropdowns for filter values/options
    // without specifying any filter value yet.

    const initialFiltersArray: ColumnFiltersState = React.useMemo(() => {
        return Object.entries(initialQueryFilters).map(([key, value]) => {
            return {
                id: key,
                value: value?.includes(',') ? value.split(',') : value,
            };
        });
    }, [initialQueryFilters]);

    function normalizeFilterValue(value: any) {
        if (value && typeof value === 'object') {
            return value;
        }

        if (typeof value === 'string') {
            const maybeDate = new Date(value);
            if (!isNaN(maybeDate.getTime()) && value.includes('T')) {
                return maybeDate;
            }

            return value;
        }

        if (Array.isArray(value)) {
            return value.map((v) => {
                const maybeDate = new Date(v);
                if (!isNaN(maybeDate.getTime()) && v.includes('T')) {
                    return maybeDate;
                }
                return v;
            });
        }

        return value;
    }

    const urlFilters = React.useMemo<ColumnFiltersState>(() => {
        const filters = searchParams.get('filters');

        if (!filters) return initialFiltersArray;

        try {
            const parsed = JSON.parse(filters);

            return parsed.map((f: any) => ({
                id: f.id,
                value: normalizeFilterValue(f.value),
            }));
        } catch {
            return initialFiltersArray;
        }
    }, []);

    const [columnFiltersState, setColumnFiltersState] = React.useState<ColumnFiltersState>(() => urlFilters);
    const [columnFiltersShowDropdownState, setColumnFiltersShowDropdownState] =
        React.useState<ColumnFilterShowDropdownState>(() => urlFilters.map((f) => f.id));

    const queryFiltersOptions = React.useMemo(() => {
        return columnFiltersState.reduce((acc, filter) => {
            // If we don't show the dropdown, we don't want to include the filter in the query
            if (!columnFiltersShowDropdownState.includes(filter.id)) return acc;
            if (typeof filter.value === 'object') {
                const {
                    value: { beforeDate, afterDate },
                    id,
                } = filter as FilterObject;

                if (id === 'inline_date_filters') {
                    const safeFromDate = new Date(afterDate);
                    const safeToDate = new Date(beforeDate);

                    const from = afterDate && !isNaN(safeFromDate.getTime()) ? safeFromDate.toISOString() : undefined;
                    const to = beforeDate && !isNaN(safeToDate.getTime()) ? safeToDate.toISOString() : undefined;

                    return {
                        ...acc,
                        ...(from ? { from } : {}),
                        ...(to ? { to } : {}),
                    };
                }

                if (Object.hasOwn(filter.value as object, 'beforeDate')) {
                    const {
                        value: { beforeDate, afterDate },
                        id,
                    } = filter as FilterObject;

                    const safeAfterDate = new Date(afterDate);
                    const safeBeforeDate = new Date(beforeDate);

                    const afterProp = `${[id]}_after`;
                    const beforeProp = `${[id]}_before`;

                    const idsWithYYYYMMDDFormat = [
                        'exit_time',
                        'entry_time',
                        'valid_from',
                        'valid_to',
                        'billing_period_start',
                        'billing_period_end',
                    ];

                    const afterValue = idsWithYYYYMMDDFormat.includes(id)
                        ? !isNaN(safeAfterDate.getTime())
                            ? safeAfterDate.toISOString().split('T')[0]
                            : undefined
                        : !isNaN(safeAfterDate.getTime())
                          ? safeAfterDate.toISOString().split('.')[0] + 'Z'
                          : undefined;

                    const beforeValue = idsWithYYYYMMDDFormat.includes(id)
                        ? !isNaN(safeBeforeDate.getTime())
                            ? safeBeforeDate.toISOString().split('T')[0]
                            : undefined
                        : !isNaN(safeBeforeDate.getTime())
                          ? safeBeforeDate.toISOString().split('.')[0] + 'Z'
                          : undefined;

                    return { ...acc, [afterProp]: afterValue, [beforeProp]: beforeValue };
                }
            }

            if (filter.id === 'date_greater_than') {
                const targetDate = new Date(filter.value as Date);
                const date_greater_than =
                    filter.value && !isNaN(targetDate.getTime()) ? targetDate.toISOString() : undefined;
                return {
                    ...acc,
                    ...(date_greater_than ? { date_greater_than } : {}),
                };
            }
            if (filter.id === 'date_less_than') {
                const targetDate = new Date(filter.value as Date);
                const date_less_than =
                    filter.value && !isNaN(targetDate.getTime()) ? targetDate.toISOString() : undefined;

                return {
                    ...acc,
                    ...(date_less_than ? { date_less_than } : {}),
                };
            }
            return { ...acc, [filter.id]: filter.value };
        }, {});
    }, [columnFiltersState, columnFiltersShowDropdownState]);

    return {
        columnFiltersState,
        setColumnFiltersState,
        columnFiltersShowDropdownState,
        setColumnFiltersShowDropdownState,
        queryFiltersOptions,
    };
}

export default useFilters;
